import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import ElevatedButton from './ElevatedButton';

const NavbarContainer = styled.nav`
  box-sizing: border-box;
  position: fixed;
  top: 2rem;
  left: 50%;
  transform: translateX(-50%);
  background: ${props => props.theme.cardBackground};
  backdrop-filter: blur(20px);
  border: 1px solid ${props => props.theme.border};
  border-radius: 50px;
  padding: ${props => props.isScrolled ? '0.5rem 1.5rem' : '0.75rem 2rem'};
  z-index: 1000;
  transition: all 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);

  width: ${props => props.isScrolled ? '55%' : '72%'};
  max-width: ${props => props.isScrolled ? '700px' : '1000px'};

  @media (max-width: 1200px) {
    width: ${props => props.isScrolled ? '65%' : '80%'};
    padding: ${props => props.isScrolled ? '0.5rem 1.25rem' : '0.75rem 1.5rem'};
  }

  @media (max-width: 992px) {
    width: ${props => props.isScrolled ? '75%' : '85%'};
    padding: ${props => props.isScrolled ? '0.5rem 1rem' : '0.75rem 1.25rem'};
  }

  @media (max-width: 768px) {
    width: ${props => props.isScrolled ? '85%' : '90%'};
    padding: 0.5rem 1.5rem;
    top: 1rem;
  }

  @media (max-width: 480px) {
    width: ${props => props.isScrolled ? '95%' : '95%'};
    padding: 0.5rem 1rem;
  }
`;

const NavbarRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 1rem;
`;

const NavLinks = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${props => props.isScrolled ? '0.5rem' : '2rem'};
  transition: gap 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55);

  @media (max-width: 800px) {
    display: none;
  }

  @media (max-width: 1200px) {
    gap: ${props => props.isScrolled ? '0.4rem' : '1.5rem'};
  }

  @media (max-width: 992px) {
    gap: ${props => props.isScrolled ? '0.3rem' : '1rem'};
  }

  @media (max-width: 768px) {
    gap: 1rem;
  }

  @media (max-width: 480px) {
    gap: 0.5rem;
  }
`;

const MobileToggle = styled.button`
  display: none;
  background: transparent;
  border: 1px solid ${props => props.theme.border};
  color: ${props => props.theme.textSecondary};
  border-radius: 500px;
  width: 42px;
  height: 42px;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    color: ${props => props.theme.navTextHover};
    border-color: ${props => props.theme.navTextHover};
  }

  @media (max-width: 800px) {
    display: inline-flex;
  }
`;

const MobileMenu = styled.div`
  display: none;

  @media (max-width: 800px) {
    display: ${props => props.$isOpen ? 'flex' : 'none'};
    flex-direction: column;
    gap: 0.5rem;
    margin-top: 0.75rem;
    padding-top: 0.75rem;
    border-top: 1px solid ${props => props.theme.border};
  }
`;

const MobileLink = styled(Link)`
  color: ${props => props.theme.textSecondary};
  text-decoration: none;
  font-weight: 500;
  padding: 0.55rem 0.25rem;
  border-radius: 8px;

  &:hover {
    color: ${props => props.theme.navTextHover};
    background: ${props => props.theme.cardBackgroundHover};
  }
`;

const MobileButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding-bottom: 1rem;
  align-items: center;
`;

const NavLink = styled(Link)`
  color: ${props => props.theme.text};
  text-decoration: none;
  font-weight: 500;
  font-size: ${props => props.isScrolled ? '0.9rem' : '1rem'};
  padding: ${props => props.isScrolled ? '0.35rem 0.75rem' : '0.5rem 1rem'};
  border-radius: 25px;
  transition: all 0.3s ease;
  white-space: nowrap;
  background: ${props => props.$active ? props.theme.buttonPrimary + '20' : 'transparent'};
  color: ${props => props.$active ? props.theme.buttonPrimary : props.theme.text};

  &:hover {
    background: ${props => props.theme.buttonPrimary};
    color: white;
    transform: translateY(-1px);
  }

  @media (max-width: 1200px) {
    font-size: ${props => props.isScrolled ? '0.85rem' : '0.95rem'};
    padding: ${props => props.isScrolled ? '0.3rem 0.65rem' : '0.45rem 0.9rem'};
  }

  @media (max-width: 992px) {
    font-size: ${props => props.isScrolled ? '0.8rem' : '0.9rem'};
    padding: ${props => props.isScrolled ? '0.25rem 0.55rem' : '0.4rem 0.8rem'};
  }

  @media (max-width: 768px) {
    font-size: 0.9rem;
    padding: 0.4rem 0.8rem;
  }

  @media (max-width: 480px) {
    font-size: 0.8rem;
    padding: 0.3rem 0.6rem;
  }
`;

const Logo = styled(Link)`
  font-size: ${props => props.isScrolled ? '1.2rem' : '1.5rem'};
  font-weight: 700;
  color: ${props => props.theme.buttonPrimary};
  text-decoration: none;
  margin-right: ${props => props.isScrolled ? '0.5rem' : '1rem'};
  transition: all 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55);

  &:hover {
    color: ${props => props.theme.buttonPrimary};
    text-decoration: none;
  }

  @media (max-width: 1200px) {
    font-size: ${props => props.isScrolled ? '1.15rem' : '1.4rem'};
    margin-right: ${props => props.isScrolled ? '0.4rem' : '0.8rem'};
  }

  @media (max-width: 992px) {
    font-size: ${props => props.isScrolled ? '1.1rem' : '1.3rem'};
    margin-right: ${props => props.isScrolled ? '0.35rem' : '0.6rem'};
  }

  @media (max-width: 768px) {
    font-size: 1.25rem;
    margin-right: 0.5rem;
  }

  @media (max-width: 480px) {
    font-size: 1.1rem;
    margin-right: 0.25rem;
  }
`;

const StyledLink = styled(Link)`
  text-decoration: none;
`;

const PublicNavbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    let scrollableContainer = null;
    let isListening = false;

    const handleScroll = () => {
      if (scrollableContainer) {
        setIsScrolled(scrollableContainer.scrollTop > 50);
      } else {
        const scrollTop = window.scrollY || document.documentElement.scrollTop || 0;
        setIsScrolled(scrollTop > 50);
      }
    };

    const findAndAttachContainer = () => {
      if (!scrollableContainer) {
        const containers = document.querySelectorAll('div');
        // Find the main scrollable container (usually the largest one with overflow)
        let maxClientHeight = 0;
        
        for (let container of containers) {
          const style = window.getComputedStyle(container);
          if ((style.overflowY === 'auto' || style.overflowY === 'scroll') && 
              container.scrollHeight > container.clientHeight) {
            
            // Prefer the largest scrollable container (likely the page wrapper)
            if (container.clientHeight > maxClientHeight) {
              maxClientHeight = container.clientHeight;
              scrollableContainer = container;
            }
          }
        }
      }

      if (scrollableContainer && !isListening) {
        scrollableContainer.addEventListener('scroll', handleScroll, { passive: true });
        isListening = true;
        // Set initial state
        handleScroll();
      } else if (!scrollableContainer && !isListening) {
        // Fallback to window scroll if no container found yet
        // This handles cases where the body itself is scrolling
        window.addEventListener('scroll', handleScroll, { passive: true });
        isListening = true;
        handleScroll();
      }
    };

    // Try to find and attach immediately
    findAndAttachContainer();

    // Also try after delays in case DOM isn't ready
    setTimeout(findAndAttachContainer, 100);
    setTimeout(findAndAttachContainer, 500);

    // Use MutationObserver to watch for new elements
    const observer = new MutationObserver(() => {
      findAndAttachContainer();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Cleanup
    return () => {
      observer.disconnect();
      if (scrollableContainer && isListening) {
        scrollableContainer.removeEventListener('scroll', handleScroll);
      }
      window.removeEventListener('scroll', handleScroll);
    };
  }, [location.pathname]);

  useEffect(() => {
    if (window.innerWidth > 1100) {
      setIsMenuOpen(false);
    }
  }, [location.pathname]);
  
  const { isAuthenticated } = useAuth();

  return (
    <NavbarContainer isScrolled={isScrolled}>
      <NavbarRow>
        <Logo to="/" isScrolled={isScrolled}>PlanMySemester</Logo>
        <NavLinks isScrolled={isScrolled}>
          <NavLink to="/features" isScrolled={isScrolled} $active={location.pathname === '/features'}>Features</NavLink>
          <NavLink to="/pricing" isScrolled={isScrolled} $active={location.pathname === '/pricing'}>Pricing</NavLink>
          <NavLink to="/about" isScrolled={isScrolled} $active={location.pathname === '/about'}>About</NavLink>
          {isAuthenticated ? (
            <NavLink to="/dashboard" isScrolled={isScrolled}>View Dashboard</NavLink>
          ) : (
            <NavLink to="/login" isScrolled={isScrolled}>Sign In</NavLink>
          )}
          <StyledLink to="/register">
            <ElevatedButton primary size={isScrolled ? "small" : "medium"}>Get Started</ElevatedButton>
          </StyledLink>
        </NavLinks>
        <MobileToggle onClick={() => setIsMenuOpen(prev => !prev)} aria-label="Open navigation menu" aria-expanded={isMenuOpen}>
          ☰
        </MobileToggle>
      </NavbarRow>
      <MobileMenu $isOpen={isMenuOpen}>
        <MobileLink to="/features" onClick={() => setIsMenuOpen(false)}>Features</MobileLink>
        <MobileLink to="/pricing" onClick={() => setIsMenuOpen(false)}>Pricing</MobileLink>
        <MobileLink to="/about" onClick={() => setIsMenuOpen(false)}>About</MobileLink>
        {isAuthenticated ? (
          <MobileLink to="/dashboard" onClick={() => setIsMenuOpen(false)}>View Dashboard</MobileLink>
        ) : (
          <MobileLink to="/login" onClick={() => setIsMenuOpen(false)}>Sign In</MobileLink>
        )}
        <MobileButtonRow>
          <StyledLink to="/register" onClick={() => setIsMenuOpen(false)}>
            <ElevatedButton primary size="small">Get Started</ElevatedButton>
          </StyledLink>
        </MobileButtonRow>
      </MobileMenu>
    </NavbarContainer>
  );
};

export default PublicNavbar;
